import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { SplatMesh, SparkRenderer, dyno } from '@sparkjsdev/spark';
import { SceneControls } from './controls/SceneControls';
import { SightManager } from './camera/SightManager';
import { CameraDirector } from './camera/CameraDirector';
import type { CameraConfig } from '../cameraConfig';
import { CollisionSystem } from './collision/CollisionSystem';
import type { CollisionConfig } from './collision/CollisionSystem';
import type { Sight, SightId } from '../types';
import { $appConfig, updateCameraConfig, $previewModelConfig, updatePreviewModelConfig, $backgroundConfig, $tellerAssistantConfig, updateTellerAssistantConfig } from '../state/appConfig';
import { $viewMode } from '../state/viewMode';
import { TellerAssistantShader } from './assistant/TellerAssistantShader';
import type { AppConfig } from '../state/appConfig';
import type { DevConfig } from '../devConfig';
import type { PoiConfig } from '../poiConfig';
import type { TellerAssistantConfig } from '../tellerAssistantConfig';
import {
  $currentSight,
  $orbitAnimationEnabled,
  clearOrbitTargetOverride,
  userSelectedSight,
  setOrbitAnimationEnabled,
  userInteracted,
} from '../state/appState';
import { assistantPoiClickRequested } from '../state/assistantHud';
import { getCurtainOffset } from '../context/CameraViewOffsetContext';
import { $safeAreaInsets } from '../state/safeArea';
import {
  sceneLoadingFailed,
  sceneLoadingFinished,
  sceneLoadingProgressed,
  sceneLoadingStarted,
  scenePreviewLoadingFinished,
  splatLoadingProgressed,
  splatFetchCompleted,
  splatInitCompleted,
} from '../state/sceneLoading';
import { detectQualityProfile, type QualityProfile } from './qualityProfile';
import { PrincipalPointController } from './camera/PrincipalPointController';
import { EnvironmentManager } from './environment/EnvironmentManager';
import { PoiMarkerManager, type PoiMarkerDiagnostics } from './poi/PoiMarkerManager';
import { DevVisuals } from './dev/DevVisuals';
import { RearViewSparkModule } from './spark/RearViewSparkModule';
import { PreviewModel } from './appearance/PreviewModel';
import { PreviewTour } from './camera/PreviewTour';
import type { SceneConfig, SceneTransform } from './sceneConfig';
export type { PoiMarkerDiagnostics } from './poi/PoiMarkerManager';

type ProgressCallback = (loadedBytes: number, totalBytes: number | null) => void;

const DEFAULT_OBJECT_SCALE = new THREE.Vector3(1, 1, 1);

async function readResponseBytesWithProgress(
  response: Response,
  onProgress?: ProgressCallback
): Promise<Uint8Array> {
  const contentLength = response.headers.get('content-length');
  const totalBytes = typeof contentLength === 'string' ? Number(contentLength) || null : null;

  if (!response.body) {
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    onProgress?.(bytes.byteLength, totalBytes ?? bytes.byteLength);
    return bytes;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loadedBytes = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value) {
      chunks.push(value);
      loadedBytes += value.length;
      onProgress?.(loadedBytes, totalBytes);
    }
  }

  const merged = new Uint8Array(loadedBytes);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }

  onProgress?.(loadedBytes, totalBytes ?? loadedBytes);
  return merged;
}

async function fetchBinaryWithProgress(
  url: string,
  onProgress?: ProgressCallback
): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }

  return readResponseBytesWithProgress(response, onProgress);
}

export interface SceneBindings {
  camera: THREE.PerspectiveCamera;
  controls: SceneControls;
  sightManager: SightManager;
  renderer: THREE.WebGLRenderer;
  sparkRenderer: SparkRenderer;
  scene: THREE.Scene;
  getPoiMarkerDiagnostics: () => PoiMarkerDiagnostics[];
  tellerAssistant: TellerAssistantShader | null;
}

interface SceneOptions {
  container: HTMLElement;
  sights: Sight[];
  config: SceneConfig;
  onReady?: (bindings: SceneBindings) => void;
}

export function createScene({
  container,
  sights,
  config,
  onReady,
}: SceneOptions): () => void {
  const qualityProfile = detectQualityProfile();

  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    powerPreference: 'high-performance',
    stencil: false,
    depth: true,
  });
  renderer.setClearColor(config.renderer.clearColor, 1);

  container.innerHTML = '';
  let canvasWidth = window.innerWidth;
  let canvasHeight = window.innerHeight;

  const clock = new THREE.Clock();
  const fbxLoader = new FBXLoader();
  const urlParams = new URL(window.location.href).searchParams;
  const isDevMode = import.meta.env.DEV || urlParams.has('dev');
  const initialAppConfig = $appConfig.getState();
  let latestCameraConfig: CameraConfig = initialAppConfig.camera;
  let currentDevConfig: DevConfig = initialAppConfig.dev;
  let latestTellerAssistantConfig: TellerAssistantConfig = initialAppConfig.tellerAssistant;
  let hasAppliedInitialCameraViewOffset = false;
  const isMobile = qualityProfile.name === 'mobile';

  const scene = new THREE.Scene();

  const devVisuals = new DevVisuals(scene, isDevMode);
  const collisionSystem = new CollisionSystem(initialAppConfig.collision);
  const skyboxConfig = config.environment.background.skybox;
  const initialSkyboxUrl = skyboxConfig
    ? (isMobile ? (skyboxConfig.mobileUrl ?? skyboxConfig.url) : skyboxConfig.url)
    : undefined;

  const environmentManager = new EnvironmentManager({
    scene,
    fbxLoader,
    collisionSystem,
    isDevMode,
    config: config.environment,
    initialSkyboxUrl,
    onCollisionDebugEdges: (edges) => devVisuals.registerCollisionEdges(edges),
  });
  environmentManager.addLighting();

  const poiManager = new PoiMarkerManager({
    container,
    gestureTarget: renderer.domElement,
    collisionSystem,
    onNavigateToSight: (sightId: SightId) => {
      userSelectedSight({ id: sightId, smooth: true, source: 'poi' });
      assistantPoiClickRequested({ id: sightId });
    },
    initialConfig: initialAppConfig.poi,
  });

  const sparkRenderer = new SparkRenderer({
    renderer,
    clock,
    autoUpdate: false,
    preUpdate: false,
    originDistance: qualityProfile.spark.originDistance,
    maxStdDev: qualityProfile.spark.maxStdDev,
    minPixelRadius: qualityProfile.spark.minPixelRadius,
    maxPixelRadius: qualityProfile.spark.maxPixelRadius,
    minAlpha: qualityProfile.spark.minAlpha,
    view: {
      autoUpdate: false,
      stochastic: qualityProfile.spark.stochastic,
    },
  });
  scene.add(sparkRenderer);
  sparkRenderer.renderOrder = -1;

  const camera = new THREE.PerspectiveCamera(
    config.camera.fov,
    window.innerWidth / window.innerHeight,
    config.camera.near,
    config.camera.far
  );
  camera.position.set(
    config.camera.initialPosition.x,
    config.camera.initialPosition.y,
    config.camera.initialPosition.z
  );
  scene.add(camera);
  const rearViewModule = new RearViewSparkModule({
    scene,
    camera,
    sparkRenderer,
  });

  const principalPointController = new PrincipalPointController(camera, clock);
  
  // Teller assistant is created lazily — only when showTellerAssistant is enabled.
  // This avoids allocating GPU resources (geometry, materials, textures) when the
  // assistant is not needed.
  let tellerAssistant: TellerAssistantShader | null = null;
  let createTellerAssistant: (() => Promise<void>) | null = null;

  const setRendererSize = () => {
    const pixelRatio = Math.min(window.devicePixelRatio ?? 1, qualityProfile.pixelRatioCap);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    const canvas = renderer.domElement;
    canvasWidth = canvas.clientWidth || window.innerWidth;
    canvasHeight = canvas.clientHeight || window.innerHeight;
    camera.aspect = canvasWidth / Math.max(1, canvasHeight);
    camera.updateProjectionMatrix();
    principalPointController.setCanvasSize(canvasWidth, canvasHeight);
  };
  setRendererSize();
  container.appendChild(renderer.domElement);
  renderer.domElement.style.touchAction = 'none';
  const controls = new SceneControls(camera, renderer.domElement);
  const collisionSystemRef = collisionSystem;
  controls.collisionSystem = collisionSystemRef;
  const cameraDirector = new CameraDirector(camera, controls, initialAppConfig.camera);

  const previewTour = new PreviewTour({ cameraDirector, clock, fov: config.camera.fov });

  const sightManager = new SightManager(
    camera,
    cameraDirector,
    initialAppConfig.camera
  );
  controls.onOrbitInteractionStart = () => {
    sightManager.stopOrbitAnimation();
    setOrbitAnimationEnabled(false);
    userInteracted();
    if (latestCameraConfig.orbitAutoMove) {
      updateCameraConfig({ orbitAutoMove: false });
    }
  };

  const orbitModeCenter = new THREE.Vector3();

  const updateOrbitCenter = (): void => {
    const source = currentDevConfig.orbitCenterOverride ?? latestCameraConfig.orbitCenter;
    if (!source) {
      return;
    }
    orbitModeCenter.set(source.x, source.y, source.z);
    controls.setOrbitCenter(orbitModeCenter);
    devVisuals.setOrbitCenter(orbitModeCenter);
    previewTour.setOrbitCenter(orbitModeCenter);
  };

  const applyCameraConfig = (config: CameraConfig): void => {
    latestCameraConfig = config;
    camera.fov = config.fov;
    camera.updateProjectionMatrix();
    controls.applyCameraConfig(config);
    sightManager.setCameraConfig(config);
    devVisuals.updateGridHeights(config);
    updateOrbitCenter();
    const {
      animate: animateViewOffset,
      animationDurationMs,
      ...viewOffset
    } = config.viewOffset;
    const viewOffsetAnimationDuration =
      typeof animationDurationMs === 'number'
        ? Math.max(0, animationDurationMs) / 1000
        : undefined;
    principalPointController.applyViewOffset(viewOffset, {
      animate: animateViewOffset ?? hasAppliedInitialCameraViewOffset,
      duration: viewOffsetAnimationDuration,
    });
    if (!hasAppliedInitialCameraViewOffset) {
      hasAppliedInitialCameraViewOffset = true;
    }

    // Re-position teller assistant when viewOffset changes (curtain open/close)
    updateTellerAssistantPosition();
  };

  const applyCollisionConfig = (config: CollisionConfig): void => {
    collisionSystemRef.updateConfig(config);
  };

  const applyDevConfig = (config: DevConfig): void => {
    currentDevConfig = config;
    devVisuals.applyDevConfig(config);
    if (config.showTellerAssistant) {
      if (tellerAssistant) {
        // Only make the assistant visible if the scene is fully revealed;
        // otherwise it will be enabled by splatAnimationTick after the reveal.
        tellerAssistant.setEnabled(sceneLoadingFinishedEmitted);
      } else if (createTellerAssistant) {
        void createTellerAssistant();
      }
    } else {
      tellerAssistant?.setEnabled(false);
    }
    rearViewModule.setEnabled(Boolean(config.showRearView));
    updateOrbitCenter();
  };

  const applyPoiConfig = (config: PoiConfig): void => {
    poiManager.setConfig(config);
  };

  /**
   * Update the teller assistant position based on current viewport and config.
   * The ball is anchored to the curtain (chat panel) top boundary via
   * viewOffset.bottom, with an additional verticalOffset from responsive config.
   * Also passes viewOffset.top so the shader can compensate for the camera's
   * principal-point shift (which would otherwise displace the ball on screen).
   */
  const updateTellerAssistantPosition = (): void => {
    if (!tellerAssistant) {
      return;
    }
    const responsiveConfig = isMobile
      ? latestTellerAssistantConfig.transform.mobile
      : latestTellerAssistantConfig.transform.desktop;

    const curtainBoundaryPx = latestCameraConfig.viewOffset.enabled
      ? latestCameraConfig.viewOffset.bottom
      : 0;

    const viewOffsetTopPx = latestCameraConfig.viewOffset.enabled
      ? (latestCameraConfig.viewOffset.top ?? 0)
      : 0;

    tellerAssistant.updatePositionFromViewport(
      canvasWidth,
      canvasHeight,
      latestCameraConfig.fov,
      isMobile,
      curtainBoundaryPx,
      viewOffsetTopPx,
      responsiveConfig
    );
  };

  const applyTellerAssistantConfig = (config: TellerAssistantConfig): void => {
    latestTellerAssistantConfig = config;
    if (!tellerAssistant) {
      return;
    }

    // Update position using responsive settings
    updateTellerAssistantPosition();

    // Update mesh params
    tellerAssistant.updateMeshParams({
      gooScale: config.mesh.gooScale,
      gooAmount: config.mesh.gooAmount,
      gooSpeed: config.mesh.gooSpeed,
      gooPoleAmount: config.mesh.gooPoleAmount,
      waveScale: config.mesh.waveScale,
      waveFrequency: config.mesh.waveFrequency,
      waveAmount: config.mesh.waveAmount,
      waveSpeed: config.mesh.waveSpeed,
      wavePoleAmount: config.mesh.wavePoleAmount,
      color: config.mesh.color,
      rotationSpeed: config.mesh.rotationSpeed,
      showNormals: config.mesh.showNormals,
      hoverAmplitude: config.transform.hoverAmplitude,
    });

    // Update shell params
    tellerAssistant.updateShellParams({
      particlesCount: config.shell.particlesCount,
      particlesColor: config.shell.particlesColor,
      particlesSize: config.shell.particlesSize,
      particlesRadius: config.shell.particlesRadius,
      particlesNoiseScale: config.shell.particlesNoiseScale,
      particlesNoiseSpeed: config.shell.particlesNoiseSpeed,
      particlesNoiseAmount: config.shell.particlesNoiseAmount,
    });

    // Update lighting
    tellerAssistant.updateLighting(config.lighting.lightingType, {
      lightX: config.lighting.simple.lightX,
      lightY: config.lighting.simple.lightY,
      lightZ: config.lighting.simple.lightZ,
      ambientLightColor: config.lighting.simple.ambientLightColor,
      directionalLightColor: config.lighting.simple.directionalLightColor,
      ambientLightIntensity: config.lighting.simple.ambientLightIntensity,
      directionalLightIntensity: config.lighting.simple.directionalLightIntensity,
      environmentLightingIntensity: config.lighting.env.environmentLightingIntensity,
      environmentRotationY: config.lighting.env.environmentRotationY,
    }).catch((error) => {
      console.error('Failed to update lighting:', error);
    });
  };

  const applyAppConfig = (config: AppConfig): void => {
    applyCameraConfig(config.camera);
    applyCollisionConfig(config.collision);
    applyDevConfig(config.dev);
    applyPoiConfig(config.poi);
    applyTellerAssistantConfig(config.tellerAssistant);
  };

  applyAppConfig(initialAppConfig);

  // Apply initial viewOffset immediately to avoid camera jump when React components mount.
  // This ensures the principal point is already shifted before any transitions start.
  const safeAreaInsets = $safeAreaInsets.getState();
  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  const initialHiddenOffset = getCurtainOffset(
    'hidden',
    viewportHeight,
    0,
    safeAreaInsets
  );
  updateCameraConfig({
    viewOffset: {
      enabled: true,
      top: 0,
      right: 0,
      left: 0,
      bottom: initialHiddenOffset,
      animate: false,
    },
  });

  const lastCameraPosition = camera.position.clone();
  const lastCameraQuaternion = camera.quaternion.clone();
  const cameraPositionThresholdSq = qualityProfile.cameraPositionThreshold ** 2;
  const cameraRotationThreshold = qualityProfile.cameraRotationThreshold;
  let needsSparkUpdate = true;
  let isDisposed = false;
  let animationFrameId: number | null = null;
  let splatLoaded = false;
  let sceneLoadingFinishedEmitted = false;
  const splatAnimationTime = dyno.dynoFloat(0);
  const splatAnimationEndTime = config.splat.revealAnimationDuration;

  function markSparkDirty(): void {
    needsSparkUpdate = true;
  }

  function requestImmediateSparkUpdate(): void {
    sparkRenderer.needsUpdate = true;
    markSparkDirty();
    sparkRenderer.update({
      scene,
      viewToWorld: camera.matrixWorld,
    });
  }

  const splatParam = urlParams.get('splat');
  const splatSelection = resolveSplatSelection(splatParam, config.splat, qualityProfile);
  const splatURL = splatSelection.url;
  const splatVariant = splatSelection.variant;
  if (splatVariant?.skybox) {
    const variantSkyboxUrl = isMobile
      ? (splatVariant.skybox.mobileUrl ?? splatVariant.skybox.url)
      : splatVariant.skybox.url;
    environmentManager.setSkyboxUrl(variantSkyboxUrl);
    const variantIntensity = isMobile
      ? (splatVariant.skybox.mobileIntensity ?? splatVariant.skybox.intensity)
      : splatVariant.skybox.intensity;
    if (variantIntensity != null) {
      environmentManager.setSkyboxIntensity(variantIntensity);
    }
  }
  let splatMesh: SplatMesh | null = null;
  sceneLoadingStarted();

  const previewModel = new PreviewModel({
    scene,
    options: {
      url: config.previewModel.url,
      transform: toPreviewTransform(config.previewModel.transform),
      onLoadProgress: sceneLoadingProgressed,
      style: {
        revealDistanceScale: config.splat.revealDistanceScale,
        ...config.previewModel.style,
      },
    },
  });

  // Initialize preview model config store with values from scene config
  const previewStyle = config.previewModel.style ?? {};
  updatePreviewModelConfig({
    wavesCenter: previewStyle.wavesCenter ?? { x: 0, y: 0, z: 0 },
    wavesMinDistance: previewStyle.wavesMinDistance ?? 0,
    wavesAmplitude: previewStyle.wavesAmplitude ?? 3.0,
    wavesPeriod: previewStyle.wavesPeriod ?? 1.0,
    wavesSpeed: previewStyle.wavesSpeed ?? 1.0,
    wavesColorIntensity: previewStyle.wavesColorIntensity ?? 5.0,
    wavesDisplacement: previewStyle.wavesDisplacement ?? 0.01,
  });

  const splatMeshPromise = (async () => {
    const fileBytes = await fetchBinaryWithProgress(
      splatURL,
      (loaded, total) => {
        splatLoadingProgressed({ loadedBytes: loaded, totalBytes: total });
      },
    );
    splatFetchCompleted();

    await new Promise<void>((resolve, reject) => {
      splatMesh = new SplatMesh({
        fileBytes,
        fileName: splatURL,
        onLoad: async (mesh) => {
          try {
            await initSplat(mesh);
            poiManager.createMarkers(sights);
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        objectModifier: dyno.dynoBlock(
          { gsplat: dyno.Gsplat },
          { gsplat: dyno.Gsplat },
          ({ gsplat }) => {
            // Get splat scale to convert revealDistanceScale from world to local coordinates
            const splatScale = splatVariant?.transform?.scale?.x 
              ?? config.splat.defaultTransform?.scale?.x 
              ?? 1.0;
            // revealDistanceScale is defined in world coordinates
            // For local coordinates: localDistanceScale = worldDistanceScale * splatScale
            const worldDistanceScale = config.splat.revealDistanceScale ?? 2.5;
            const localDistanceScale = worldDistanceScale * splatScale;

            const d = new dyno.Dyno({
              inTypes: { gsplat: dyno.Gsplat, t: "float", revealOriginLocal: "vec3", distanceScale: "float" },
              outTypes: { gsplat: dyno.Gsplat },
              statements: ({ inputs, outputs }) => dyno.unindentLines(`
            ${outputs.gsplat} = ${inputs.gsplat};
            float t = ${inputs.t};
            vec3 localPos = ${inputs.gsplat}.center;
            // Calculate distance from reveal origin in local coordinates (using xz plane)
            vec3 offsetFromOrigin = localPos - ${inputs.revealOriginLocal};
            float l = length(offsetFromOrigin.xz);
            // Simple reveal effect: fade in based on distance from origin
            // distanceScale is in local coordinates (world scale * mesh scale)
            float tttt = pow(t, 4.0)*.4+.5;
            float visibility = clamp(tttt - l * ${inputs.distanceScale}, 0., 1.);
            // Use visibility only for alpha, keep original colors
            vec4 originalRgba = ${inputs.gsplat}.rgba;
            ${outputs.gsplat}.rgba = vec4(originalRgba.rgb, originalRgba.a * visibility);
          `),
            });

            // Convert revealOrigin from world to local coordinates of mesh
            // This accounts for mesh position and rotation
            let revealOriginLocal = camera.position.clone();
            if (splatMesh) {
              // Update mesh matrix to get current transformation
              splatMesh.updateMatrixWorld(false);
              // Transform world position to local coordinates using matrix
              revealOriginLocal.applyMatrix4(splatMesh.matrixWorld.clone().invert());
            }

            gsplat = d.apply({
              gsplat,
              t: splatAnimationTime,
              revealOriginLocal: dyno.dynoVec3([revealOriginLocal.x, revealOriginLocal.y, revealOriginLocal.z]),
              distanceScale: dyno.dynoFloat(localDistanceScale),
            }).gsplat;

            return { gsplat };
          }
        )
      });

      splatMesh.position.set(0, 0, 0);
      scene.add(splatMesh);
      markSparkDirty();
    });
  })();

  async function initSplat(mesh: SplatMesh): Promise<void> {
    // We hide mesh until camera is stopped, because we use camera position as reveal effect origin.
    mesh.visible = false;

    await previewTour.stop();

    applySplatTransform(mesh, config.splat.defaultTransform, splatVariant);

    let collisionCreated = false;
    try {
      const splatBox =
        typeof mesh.getBoundingBox === 'function' ? mesh.getBoundingBox(false) : null;
      const box = splatBox ?? new THREE.Box3().setFromObject(mesh);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      if (!box.isEmpty() && size.lengthSq() > 0) {
        environmentManager.createCollisionVolume(size, center, mesh, { addMeshPosition: true });
        collisionCreated = true;
      }
    } catch (error) {
      console.error('Error creating precise collision box, falling back to default:', error);
    }

    if (!collisionCreated) {
      console.warn(
        'Could not calculate bounding box for splat mesh, using default collision box'
      );
      const fallbackSize = config.environment.collisionVolume.fallbackSize;
      const size = new THREE.Vector3(
        fallbackSize.x,
        fallbackSize.y,
        fallbackSize.z
      );
      const center = mesh.position.clone();
      environmentManager.createCollisionVolume(size, center, mesh);
    }

    previewModel.setRevealOrigin(camera.position.clone());
    mesh.visible = true;
    splatLoaded = true;
    requestImmediateSparkUpdate();
    splatInitCompleted();
  }

  const environmentMeshesPromise = environmentManager.loadEnvironmentMeshes();

  createTellerAssistant = async (): Promise<void> => {
    if (tellerAssistant || isDisposed) {
      return;
    }

    const responsiveConfig = isMobile
      ? latestTellerAssistantConfig.transform.mobile
      : latestTellerAssistantConfig.transform.desktop;

    tellerAssistant = new TellerAssistantShader({
      anchor: camera,
      scene,
      renderer,
      position: latestTellerAssistantConfig.transform.position,
      rotationY: latestTellerAssistantConfig.transform.rotationY,
      scale: responsiveConfig.scale,
      useEnvironmentLighting: latestTellerAssistantConfig.lighting.lightingType === 'env',
      environmentLightingIntensity: latestTellerAssistantConfig.lighting.env.environmentLightingIntensity,
      panoramaTextureUrl: new URL('../assets/environment/sky.hdr', import.meta.url).href,
      isMobile,
    });

    await tellerAssistant.load(splatMeshPromise);
    updateTellerAssistantPosition();
    applyTellerAssistantConfig(latestTellerAssistantConfig);
    // Don't enable immediately — the teller assistant is shown only after
    // the scene reveal animation completes (see splatAnimationTick).
    // If the scene is already fully revealed (lazy creation via dev tools),
    // enable right away.
    tellerAssistant.setEnabled(sceneLoadingFinishedEmitted);
  };

  // Only create the teller assistant at startup if it is enabled in the dev config;
  // otherwise it will be created lazily when first enabled at runtime.
  const tellerAssistantPromise = currentDevConfig.showTellerAssistant
    ? createTellerAssistant()
    : Promise.resolve();

  previewModel.loadPromise
    .then(() => {
      scenePreviewLoadingFinished();
      previewTour.start();
    })
    .catch((error) => {
      console.error('Scene asset loading failed', error);
      sceneLoadingFailed();
    });

  Promise.all([splatMeshPromise, environmentMeshesPromise, tellerAssistantPromise])
    .catch((error) => {
      console.error('Scene asset loading failed', error);
      sceneLoadingFailed();
    });

  if (isDevMode) {
    devVisuals.addSightDebugMarkers(sights);
    devVisuals.applyDevConfig(currentDevConfig);
  }

  const stopConfigWatch = $appConfig.watch(applyAppConfig);
  const stopPreviewModelConfigWatch = $previewModelConfig.watch((config) => {
    previewModel.updateWavesConfig(config);
  });
  let isInitialBackgroundConfig = true;
  const stopBackgroundConfigWatch = $backgroundConfig.watch((bgConfig) => {
    // Skip initial subscription call to preserve preview color from setupBackground()
    if (isInitialBackgroundConfig) {
      isInitialBackgroundConfig = false;
      return;
    }
    environmentManager.setBackgroundColor(bgConfig.color);
    environmentManager.setSkyboxUrl(bgConfig.skyboxUrl);
    environmentManager.setSkyboxEnabled(bgConfig.skyboxEnabled);
    if (bgConfig.skyboxEnabled) {
      environmentManager.setSkyboxIntensity(bgConfig.skyboxIntensity);
      environmentManager.setSkyboxBlur(bgConfig.skyboxBlur);
      environmentManager.setSkyboxRotation(bgConfig.skyboxRotation);
    }
  });
  const stopTellerAssistantConfigWatch = $tellerAssistantConfig.watch((config) => {
    applyTellerAssistantConfig(config);
  });
  controls.setViewMode($viewMode.getState());
  const stopViewModeWatch = $viewMode.watch((mode) => {
    controls.setViewMode(mode);
  });
  const stopFocusedSightWatch = $currentSight.watch((sight) => {
    poiManager.setFocusedSightId(sight?.id ?? null);
  });
  const stopOrbitAnimationWatch = $orbitAnimationEnabled.watch((enabled) => {
    const sight = $currentSight.getState();
    sightManager.setOrbitAnimationEnabled(enabled);
    if (enabled && sight && !sightManager.isInTransition()) {
      sightManager.startOrbitAnimationForSight(sight);
    } else {
      sightManager.stopOrbitAnimation();
    }
  });
  const stopOrbitSightWatch = $currentSight.watch((sight) => {
    if (!$orbitAnimationEnabled.getState()) {
      return;
    }
    sightManager.setOrbitAnimationEnabled(true);
    if (sight && !sightManager.isInTransition()) {
      sightManager.startOrbitAnimationForSight(sight);
    } else {
      sightManager.stopOrbitAnimation();
    }
  });
  const stopOrbitTargetOverrideWatch = clearOrbitTargetOverride.watch(() => {
    sightManager.stopOrbitAnimation();
    controls.clearOrbitTargetOverride();
  });

  onReady?.({
    camera,
    controls,
    sightManager,
    renderer,
    sparkRenderer,
    scene,
    getPoiMarkerDiagnostics,
    tellerAssistant,
  });

  function onResize(): void {
    setRendererSize();
    controls.handleResize();
    principalPointController.applyViewOffset(latestCameraConfig.viewOffset, { animate: false });
    principalPointController.forceSync();
    rearViewModule.handleResize();
    updateTellerAssistantPosition();
    markSparkDirty();
  }

  function trackCameraMovement(): void {
    if (lastCameraPosition.distanceToSquared(camera.position) > cameraPositionThresholdSq) {
      lastCameraPosition.copy(camera.position);
      markSparkDirty();
    }

    const quaternionDelta = 1 - Math.abs(lastCameraQuaternion.dot(camera.quaternion));
    if (quaternionDelta > cameraRotationThreshold) {
      lastCameraQuaternion.copy(camera.quaternion);
      markSparkDirty();
    }
  }

  function splatAnimationTick(deltaT: number): void {
    if (!splatLoaded || !previewTour.completed) {
      return;
    }

    if (splatAnimationTime.value >= splatAnimationEndTime) {
      if (!sceneLoadingFinishedEmitted) {
        // First frame after reveal: dispose preview, switch background, notify UI.
        sceneLoadingFinishedEmitted = true;
        previewModel.dispose();
        environmentManager.setMainBackground();
        sceneLoadingFinished();
        // Show teller assistant now that the scene is fully revealed
        if (tellerAssistant && currentDevConfig.showTellerAssistant) {
          tellerAssistant.setEnabled(true);
        }
      } else if (splatMesh?.objectModifier) {
        // Next frame: remove the now-redundant modifier so the GPU shader
        // no longer runs on every splat. Deferred by one frame to avoid
        // a heavy updateGenerator() call on the same frame as dispose.
        splatMesh.objectModifier = undefined;
        splatMesh.updateGenerator();
        sparkRenderer.needsUpdate = true;
        markSparkDirty();
      }
      return;
    }

    splatAnimationTime.value += deltaT;
    sparkRenderer.needsUpdate = true;
    markSparkDirty();
  }

  function animate(): void {
    if (isDisposed) {
      return;
    }

    animationFrameId = requestAnimationFrame(animate);
    const delta = clock.getDelta();
    const currentTime = clock.getElapsedTime();
    sightManager.update(currentTime);
    controls.update(delta);
    cameraDirector.syncPoseFromCamera();
    tellerAssistant?.update(delta, camera);
    trackCameraMovement();
    camera.updateMatrixWorld(true);
    principalPointController.update(currentTime);
    camera.updateProjectionMatrix();
    poiManager.update(camera, canvasWidth, canvasHeight);
    if (needsSparkUpdate) {
      sparkRenderer.update({
        scene,
        viewToWorld: camera.matrixWorld,
      });
      needsSparkUpdate = false;
    }
    splatAnimationTick(delta);
    previewModel.update({
      time: currentTime,
      splatRevealTime: splatAnimationTime.value
    });
    rearViewModule.update();
    renderer.render(scene, camera);
  }

  function getPoiMarkerDiagnostics(): PoiMarkerDiagnostics[] {
    return poiManager.getDiagnostics();
  }

  window.addEventListener('resize', onResize);
  animate();

  return () => {
    if (isDisposed) {
      return;
    }
    isDisposed = true;
    stopConfigWatch();
    stopPreviewModelConfigWatch();
    stopBackgroundConfigWatch();
    stopTellerAssistantConfigWatch();
    stopViewModeWatch();
    stopFocusedSightWatch();
    stopOrbitAnimationWatch();
    stopOrbitSightWatch();
    stopOrbitTargetOverrideWatch();
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
    window.removeEventListener('resize', onResize);
    controls.dispose?.();
    tellerAssistant?.dispose();
    renderer.dispose();
    poiManager.dispose();
    devVisuals.disposeOrbitCenter();
    rearViewModule.dispose();
    if (container.contains(renderer.domElement)) {
      container.removeChild(renderer.domElement);
    }
  };
}

function resolveSplatSelection(
  splatParam: string | null,
  splatConfig: SceneConfig['splat'],
  qualityProfile: QualityProfile
): { url: string; variant: SceneConfig['splat']['variants'][number] | null } {
  const variantsByKey = new Map(
    splatConfig.variants.map((variant) => [variant.key, variant])
  );
  const variantsByAlias = new Map<string, SceneConfig['splat']['variants'][number]>();
  splatConfig.variants.forEach((variant) => {
    variant.aliases?.forEach((alias) => {
      variantsByAlias.set(alias, variant);
    });
  });

  if (splatParam) {
    const variant = variantsByKey.get(splatParam) ?? variantsByAlias.get(splatParam);
    return { url: variant?.url ?? splatParam, variant: variant ?? null };
  }

  const defaultKey = splatConfig.defaultByQuality[qualityProfile.name];
  const defaultVariant = variantsByKey.get(defaultKey) ?? splatConfig.variants[0] ?? null;
  if (!defaultVariant) {
    throw new Error('Splat configuration is missing a default variant');
  }
  return { url: defaultVariant.url, variant: defaultVariant };
}

function applySplatTransform(
  mesh: THREE.Object3D,
  defaultTransform: SceneTransform | undefined,
  variant: SceneConfig['splat']['variants'][number] | null
): void {
  resetTransform(mesh);
  const overrideDefault = Boolean(variant?.overrideDefaultTransform);
  if (defaultTransform && !overrideDefault) {
    applyTransform(mesh, defaultTransform);
  }
  if (variant?.transform) {
    if (overrideDefault) {
      resetTransform(mesh);
    }
    applyTransform(mesh, variant.transform);
  }
}

function resetTransform(object: THREE.Object3D): void {
  object.position.set(0, 0, 0);
  object.rotation.set(0, 0, 0);
  object.scale.copy(DEFAULT_OBJECT_SCALE);
}

function applyTransform(object: THREE.Object3D, transform: SceneTransform): void {
  if (transform.position) {
    object.position.set(
      transform.position.x,
      transform.position.y,
      transform.position.z
    );
  }
  if (transform.rotation) {
    object.rotation.set(
      transform.rotation.x,
      transform.rotation.y,
      transform.rotation.z
    );
  }
  if (transform.scale) {
    object.scale.set(transform.scale.x, transform.scale.y, transform.scale.z);
  }
}

function toPreviewTransform(
  transform?: SceneTransform
): { position?: THREE.Vector3; rotation?: THREE.Euler; scale?: THREE.Vector3 } | undefined {
  if (!transform) {
    return undefined;
  }
  return {
    position: transform.position
      ? new THREE.Vector3(
          transform.position.x,
          transform.position.y,
          transform.position.z
        )
      : undefined,
    rotation: transform.rotation
      ? new THREE.Euler(
          transform.rotation.x,
          transform.rotation.y,
          transform.rotation.z
        )
      : undefined,
    scale: transform.scale
      ? new THREE.Vector3(transform.scale.x, transform.scale.y, transform.scale.z)
      : undefined,
  };
}
